import { useEffect, useMemo, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { ChevronLeft, ChevronRight, Copy, Download, Maximize2, Minus, Plus } from 'lucide-react';
import type { CatalogMetadata } from '../types';
import { copyText } from '../lib/exportCatalog';

interface PublicCatalogProps {
  slug: string;
}

type FlipHandle = {
  pageFlip: () => {
    flipNext: () => void;
    flipPrev: () => void;
    turnToPage: (page: number) => void;
  };
};

export function PublicCatalog({ slug }: PublicCatalogProps) {
  const flipRef = useRef<FlipHandle | null>(null);
  const stageRef = useRef<HTMLElement | null>(null);
  const [catalog, setCatalog] = useState<CatalogMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [bookSize, setBookSize] = useState({ width: 520, height: 735 });
  const [isMobile, setIsMobile] = useState(false);
  const pages = useMemo(() => catalog?.pages ?? [], [catalog]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    fetch(`/catalogs/${slug}/metadata.json`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('File PDF non trovato');
        return response.json();
      })
      .then((metadata: CatalogMetadata) => {
        if (!isMounted) return;
        setCatalog({
          ...metadata,
          publicUrl: metadata.publicUrl?.startsWith('http')
            ? metadata.publicUrl
            : `${window.location.origin}${metadata.publicUrl || `/catalog/${slug}`}`
        });
      })
      .catch(() => {
        if (isMounted) setCatalog(null);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  useEffect(() => {
    const updateSize = () => {
      const stage = stageRef.current;
      const firstPage = pages[0];
      if (!stage || !firstPage) return;

      const rect = stage.getBoundingClientRect();
      const pageRatio = firstPage.height / firstPage.width || 1.42;
      const nextIsMobile = window.innerWidth < 760;
      setIsMobile(nextIsMobile);
      const maxPageWidth = nextIsMobile ? rect.width - 20 : (rect.width * 0.88) / 2;
      const maxPageHeight = rect.height * 0.92;
      const width = Math.max(260, Math.floor(Math.min(maxPageWidth, maxPageHeight / pageRatio)));
      const height = Math.floor(width * pageRatio);
      setBookSize({ width, height });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [pages]);

  if (isLoading) {
    return (
      <main className="publicShell">
        <div className="notFound">
          <h1>Caricamento PDF</h1>
          <p>Preparazione del PDF sfogliabile in corso.</p>
        </div>
      </main>
    );
  }

  if (!catalog) {
    return (
      <main className="publicShell">
        <div className="notFound">
          <h1>File PDF non trovato</h1>
          <p>Il link `/catalog/{slug}` non corrisponde a un file PDF creato.</p>
        </div>
      </main>
    );
  }

  const openFullscreen = () => {
    const element = document.documentElement;
    if (!document.fullscreenElement) {
      element.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <main className="readerShell">
      <header className="readerToolbar">
        <div className="readerCounter">
          <span>
            Pagina {Math.min(currentPage + 1, catalog.pageCount)} di {catalog.pageCount}
          </span>
        </div>
        <nav>
          <button title="Precedente" onClick={() => flipRef.current?.pageFlip().flipPrev()}>
            <ChevronLeft size={18} />
          </button>
          <button title="Successiva" onClick={() => flipRef.current?.pageFlip().flipNext()}>
            <ChevronRight size={18} />
          </button>
          <button title="Riduci zoom" onClick={() => setZoom((value) => Math.max(0.72, value - 0.12))}>
            <Minus size={18} />
          </button>
          <button title="Aumenta zoom" onClick={() => setZoom((value) => Math.min(2.4, value + 0.18))}>
            <Plus size={18} />
          </button>
          <button title="Fullscreen" onClick={openFullscreen}>
            <Maximize2 size={18} />
          </button>
          <button title="Copia link" onClick={() => copyText(catalog.publicUrl)}>
            <Copy size={18} />
          </button>
          {catalog.pdfUrl && (
            <a title="Scarica PDF" href={catalog.pdfUrl} download>
              <Download size={18} />
            </a>
          )}
        </nav>
      </header>

      <div className="readerLayout">
        <aside className="thumbRail">
          {pages.map((page, index) => (
            <button
              className={index === currentPage ? 'activeThumb' : ''}
              key={page.page}
              onClick={() => flipRef.current?.pageFlip().turnToPage(index)}
            >
              <img src={page.previewUrl} alt={`Pagina ${page.page}`} />
              <span>{page.page}</span>
            </button>
          ))}
        </aside>

        <section className="bookStage" ref={stageRef}>
          <div className="bookScale" style={{ transform: `scale(${zoom})` }}>
            <HTMLFlipBook
              width={bookSize.width}
              height={bookSize.height}
              size="fixed"
              minWidth={260}
              maxWidth={isMobile ? bookSize.width : bookSize.width * 2}
              minHeight={360}
              maxHeight={bookSize.height}
              showCover
              mobileScrollSupport
              className="flipBook"
              ref={flipRef as never}
              onFlip={(event: { data: number }) => setCurrentPage(event.data)}
              style={{}}
              startPage={0}
              drawShadow
              flippingTime={720}
              usePortrait={isMobile}
              startZIndex={0}
              autoSize
              maxShadowOpacity={0.22}
              clickEventForward
              useMouseEvents
              swipeDistance={20}
              showPageCorners
              disableFlipByClick={false}
            >
              {pages.map((page) => (
                <article className="page" key={page.page}>
                  <img src={page.fullUrl} alt={`Pagina ${page.page}`} />
                </article>
              ))}
            </HTMLFlipBook>
          </div>
        </section>
      </div>
    </main>
  );
}
